import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { AlertTriangle, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Button,
  Card,
  CardHeader,
  Input,
  ProviderLogo,
  ScopeChipsInput,
  type ScopeChipsInputHandle,
} from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { useSpaceContext } from '@/contexts/SpaceContext';
import { meetsRole } from '@/lib/space-roles';
import { routes } from '@/lib/routes';
import {
  useCreateMcpServer,
  useMcpServer,
  useUpdateMcpServer,
} from '@/hooks/useMcpServers';
import {
  useMcpProviderById,
  useMcpProviders,
} from '@/hooks/useMcpProviders';
import { McpProviderPicker } from './McpProviderPicker';
import {
  McpCatalogFormFields,
  resolveTemplateUrl,
} from './McpCatalogFormFields';
import { McpAuthMethodChooser, sortAuthMethods } from './McpAuthMethodChooser';
import type { CreateMcpServerInput, McpAuthMethod, McpCreateErrorCode } from '@/types';

/**
 * Pull the backend error code off a thrown ApiError. We don't import the
 * class (not exported), so duck-type instead.
 */
function errorCode(err: unknown): string | undefined {
  if (err && typeof err === 'object' && 'code' in err) {
    const c = (err as { code?: unknown }).code;
    return typeof c === 'string' ? c : undefined;
  }
  return undefined;
}

function isHttpsUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

export default function McpServerForm() {
  const navigate = useNavigate();
  const { serverId } = useParams<{ serverId?: string }>();
  const isEditing = !!serverId;
  const { spaceId, role } = useSpaceContext();
  const isAdmin = meetsRole(role, 'admin');
  const { success, error: showError } = useToast();

  const createServer = useCreateMcpServer();
  const updateServer = useUpdateMcpServer();
  const { data: existing, isLoading: existingLoading } = useMcpServer(serverId);

  const [searchParams, setSearchParams] = useSearchParams();
  const providerIdParam = searchParams.get('provider');
  const isCustom = providerIdParam === 'custom';
  const { data: catalog, isLoading: catalogLoading } = useMcpProviders();
  const provider = useMcpProviderById(
    !isCustom && providerIdParam ? providerIdParam : undefined,
  );

  const [name, setName] = useState('');
  const [serverUrl, setServerUrl] = useState('');
  const [defaultGroup, setDefaultGroup] = useState('');
  const [scopes, setScopes] = useState<string[]>([]);
  const scopesRef = useRef<ScopeChipsInputHandle>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [errorCodeState, setErrorCodeState] =
    useState<McpCreateErrorCode | null>(null);
  const [urlError, setUrlError] = useState<string | undefined>(undefined);

  // Catalog-driven state (only used when `provider` is defined). `seededFor`
  // tracks the providerId we last seeded from so switching providers in the
  // same mount re-seeds rather than carrying stale values.
  const [templateValues, setTemplateValues] = useState<Record<string, string>>(
    {},
  );
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [headerValues, setHeaderValues] = useState<Record<string, string>>({});
  const [selectedMethodKind, setSelectedMethodKind] =
    useState<McpAuthMethod['kind'] | null>(null);
  const [seededFor, setSeededFor] = useState<string | null>(null);

  // Canonical UI order — OAuth → API → Manual OAuth → unknown. Drives both
  // the chooser's render order AND the form's default-selected method, so
  // the radio that lights up on mount matches the topmost option visually.
  const sortedMethods = useMemo(
    () => (provider ? sortAuthMethods(provider.authMethods) : []),
    [provider],
  );

  // The active method object — null when no provider, or when the provider has
  // no methods (shouldn't reach the form path, but guard anyway).
  const selectedMethod: McpAuthMethod | null =
    sortedMethods.length > 0
      ? sortedMethods.find((m) => m.kind === selectedMethodKind) ??
        sortedMethods[0] ??
        null
      : null;

  // When editing, hydrate from the loaded server once.
  useEffect(() => {
    if (!isEditing || !existing) return;
    setName(existing.name);
    setServerUrl(existing.serverUrl);
    setDefaultGroup(existing.defaultGroup ?? '');
    setScopes(existing.scopes ?? []);
  }, [isEditing, existing]);

  // When creating from the catalog, seed defaults once per provider selection.
  // Switching providers via the picker re-seeds so stale values from the
  // previous provider don't bleed through.
  useEffect(() => {
    if (!provider) return;
    if (seededFor === provider.providerId) return;
    setName(provider.displayName);
    setDefaultGroup(provider.defaultGroup);
    // Default the method to the first one in canonical UI order (OAuth first),
    // not the backend's catalog order — frontend imposes the display ranking.
    const firstMethod = sortAuthMethods(provider.authMethods)[0] ?? null;
    setSelectedMethodKind(firstMethod ? firstMethod.kind : null);
    // Only seed scope selection when the first method is OAuth AND advertises
    // a full picker list. `'scopes' in m` narrows away api-key / oauth-manual
    // which don't carry a scopes spec. Switching the method re-seeds below.
    if (
      firstMethod &&
      'scopes' in firstMethod &&
      firstMethod.scopes?.available &&
      firstMethod.scopes.available.length > 0
    ) {
      setSelectedScopes([...firstMethod.scopes.recommended]);
    } else {
      setSelectedScopes([]);
    }
    if (typeof provider.serverUrl !== 'string') {
      const seeded: Record<string, string> = {};
      for (const v of provider.serverUrl.variables) {
        if (v.default) seeded[v.name] = v.default;
      }
      setTemplateValues(seeded);
    } else {
      setTemplateValues({});
    }
    setClientId('');
    setClientSecret('');
    setHeaderValues({});
    setSeededFor(provider.providerId);
  }, [provider, seededFor]);

  // When the operator switches the method (only possible for multi-method
  // providers), clear the inputs that don't apply to the new kind so stale
  // values from the old method don't get sent.
  useEffect(() => {
    if (!provider || !selectedMethod) return;
    if (selectedMethod.kind === 'api-key') {
      setSelectedScopes([]);
      setClientId('');
      setClientSecret('');
    } else {
      setHeaderValues({});
      const methodScopes = 'scopes' in selectedMethod ? selectedMethod.scopes : undefined;
      if (methodScopes?.available && methodScopes.available.length > 0) {
        setSelectedScopes([...methodScopes.recommended]);
      } else {
        setSelectedScopes([]);
      }
    }
    // Only depend on the kind — switching providers reseeds via the effect
    // above, which sets selectedMethodKind itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMethod?.kind]);

  if (!isAdmin) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold text-foreground">MCP Server</h1>
        <p className="text-sm text-muted-foreground">
          You need admin access to this space to add or edit MCP servers.
        </p>
        <Link
          to={routes.console.spaceToolsMcpServers(spaceId)}
          className="text-primary underline text-sm"
        >
          Back to MCP servers
        </Link>
      </div>
    );
  }

  if (isEditing && existingLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  const submitting = createServer.isPending || updateServer.isPending;

  const resolvedUrl = provider
    ? resolveTemplateUrl(provider, templateValues)
    : serverUrl.trim();

  const requiresManualClient =
    selectedMethod?.kind === 'oauth-manual-client';
  const manualClientReady =
    requiresManualClient
      ? clientId.trim().length > 0 && clientSecret.trim().length > 0
      : true;
  // For api-key methods, every advertised header must be filled — the runtime
  // sends them all on each call, so a missing one fails opaquely later.
  const apiKeyReady =
    selectedMethod?.kind === 'api-key'
      ? selectedMethod.headers.every(
          (h) => (headerValues[h.name] ?? '').trim().length > 0,
        )
      : true;

  const canSubmit =
    name.trim().length > 0 &&
    resolvedUrl.length > 0 &&
    !urlError &&
    manualClientReady &&
    apiKeyReady;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Commit a scope the user typed but didn't press Enter on.
    scopesRef.current?.flush();

    if (!isHttpsUrl(resolvedUrl)) {
      setUrlError('Must be a valid https:// URL');
      return;
    }
    setUrlError(undefined);
    setErrorCodeState(null);

    let input: CreateMcpServerInput;
    if (selectedMethod?.kind === 'api-key') {
      input = {
        name: name.trim(),
        serverUrl: resolvedUrl,
        defaultGroup:
          defaultGroup.trim() || provider?.defaultGroup || undefined,
        providerId: provider?.providerId,
        authMode: 'api_key',
        headers: Object.fromEntries(
          selectedMethod.headers.map((h) => [
            h.name,
            (headerValues[h.name] ?? '').trim(),
          ]),
        ),
      };
    } else if (selectedMethod?.kind === 'oauth-paste-token') {
      // No-provisioning path: backend creates the server pending and skips
      // OAuth discovery. The token is pasted afterwards via the identity
      // oauth-manual endpoint (credentials page).
      input = {
        name: name.trim(),
        serverUrl: resolvedUrl,
        defaultGroup:
          defaultGroup.trim() || provider?.defaultGroup || undefined,
        providerId: provider?.providerId,
        authMode: 'oauth_manual',
      };
    } else {
      const manualClient = requiresManualClient
        ? {
            clientId: clientId.trim(),
            clientSecret: clientSecret.trim(),
          }
        : manualOpen && clientId.trim() && clientSecret.trim()
          ? {
              clientId: clientId.trim(),
              clientSecret: clientSecret.trim(),
            }
          : undefined;

      input = {
        name: name.trim(),
        serverUrl: resolvedUrl,
        defaultGroup:
          defaultGroup.trim() || provider?.defaultGroup || undefined,
        // Empty selection from the catalog flow → omit `scopes` entirely so
        // the backend can fall back to OAuth-time discovery instead of being
        // told "request no scopes."
        scopes: provider
          ? selectedScopes.length > 0
            ? selectedScopes
            : undefined
          : scopes.length > 0
            ? scopes
            : undefined,
        manualClient,
        providerId: provider?.providerId,
      };
    }

    try {
      if (isEditing && serverId) {
        await updateServer.mutateAsync({ serverId, input });
        success('MCP server updated');
        navigate(routes.console.spaceToolsMcpServer(spaceId, serverId));
      } else {
        const created = await createServer.mutateAsync(input);
        success('MCP server created');
        navigate(routes.console.spaceToolsMcpServer(spaceId, created.serverId));
      }
    } catch (err) {
      const code = errorCode(err);
      if (
        code === 'pkce_unsupported' ||
        code === 'client_required' ||
        code === 'dcr_unsupported'
      ) {
        setErrorCodeState(code as McpCreateErrorCode);
        // For DCR-required errors, auto-expand the manualClient section so
        // the admin can fill it in without hunting for the disclosure.
        if (code === 'client_required' || code === 'dcr_unsupported') {
          setManualOpen(true);
        }
        return;
      }
      showError(err instanceof Error ? err.message : 'Failed to save server');
    }
  };

  // Picker step — render before the form when creating and no provider chosen.
  if (!isEditing && providerIdParam === null) {
    return (
      <div className="space-y-6 max-w-4xl">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Add MCP Server</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Pick a provider to get started, or connect a custom MCP-compatible
            endpoint by URL.
          </p>
        </div>
        {catalogLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="h-36 rounded-lg border border-border bg-card animate-pulse"
              />
            ))}
          </div>
        ) : (
          <McpProviderPicker
            providers={catalog?.providers ?? []}
            customServerAllowed={catalog?.customServerAllowed ?? true}
            onSelectProvider={(id) => setSearchParams({ provider: id })}
            onSelectCustom={() => setSearchParams({ provider: 'custom' })}
          />
        )}
      </div>
    );
  }

  const headerTitle = isEditing
    ? 'Edit MCP Server'
    : provider
      ? `Add ${provider.displayName}`
      : isCustom
        ? 'Add custom MCP server'
        : 'Add MCP Server';

  return (
    <div className="space-y-6 max-w-3xl">
      {!isEditing && (
        <button
          type="button"
          onClick={() => setSearchParams({})}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" /> Choose a different provider
        </button>
      )}
      <div className="flex items-center gap-4">
        {provider && (
          <ProviderLogo
            slug={provider.providerId}
            displayName={provider.displayName}
            className="h-12 w-12"
            fallbackClassName="text-sm"
          />
        )}
        <div>
          <h1 className="text-2xl font-bold text-foreground">{headerTitle}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Connect an external MCP server to this space. Each space member
            completes their own OAuth consent; the agent uses its owner's
            connection at runtime.
          </p>
        </div>
      </div>

      {errorCodeState === 'pkce_unsupported' && (
        <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/20 p-4 flex gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800 dark:text-red-200">
              OAuth provider not supported
            </p>
            <p className="text-sm text-red-700 dark:text-red-300 mt-1">
              This server's OAuth provider doesn't support PKCE and can't be
              used. Check whether the provider exposes a PKCE-compatible
              authorization endpoint, or try a different server.
            </p>
          </div>
        </div>
      )}

      {(errorCodeState === 'client_required' ||
        errorCodeState === 'dcr_unsupported') && (
        <div className="rounded-lg border border-yellow-300 bg-yellow-50 dark:border-yellow-700 dark:bg-yellow-900/20 p-4 flex gap-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
              Manual OAuth client required
            </p>
            <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
              This server doesn't support Dynamic Client Registration. Register
              an OAuth client on the server and supply its credentials below.
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader
            title="Basics"
            description="How this connection shows up in your space."
          />
          <div className="space-y-4">
            <Input
              label="Name"
              value={name}
              onChange={(e: { target: { value: string } }) =>
                setName(e.target.value)
              }
              placeholder="e.g. Linear MCP"
              required
            />
            <Input
              label="Default group"
              value={defaultGroup}
              onChange={(e: { target: { value: string } }) =>
                setDefaultGroup(e.target.value)
              }
              placeholder="Optional — group label applied to synced tools"
              hint="Tools imported from this server will land under this group in the tool catalog."
            />
          </div>
        </Card>

        {provider && selectedMethod ? (
          <>
            {sortedMethods.length > 1 && (
              <McpAuthMethodChooser
                methods={sortedMethods}
                selectedKind={selectedMethod.kind}
                onChange={setSelectedMethodKind}
              />
            )}
            <McpCatalogFormFields
              provider={provider}
              method={selectedMethod}
              ourCallbackUrl={catalog?.ourCallbackUrl ?? ''}
              templateValues={templateValues}
              setTemplateValues={setTemplateValues}
              selectedScopes={selectedScopes}
              setSelectedScopes={setSelectedScopes}
              manualClientId={clientId}
              setManualClientId={setClientId}
              manualClientSecret={clientSecret}
              setManualClientSecret={setClientSecret}
              headerValues={headerValues}
              setHeaderValues={setHeaderValues}
            />
          </>
        ) : provider ? null : (
          <>
            <Card>
              <CardHeader
                title="Server"
                description="Connection details. The URL must be an https endpoint."
              />
              <div className="space-y-4">
                <Input
                  label="Server URL"
                  value={serverUrl}
                  onChange={(e: { target: { value: string } }) => {
                    setServerUrl(e.target.value);
                    if (urlError) setUrlError(undefined);
                  }}
                  placeholder="https://mcp.example.com"
                  error={urlError}
                  required
                />
                <ScopeChipsInput
                  ref={scopesRef}
                  label="Scopes"
                  value={scopes}
                  onChange={setScopes}
                  placeholder="Optional — type a scope and press Enter"
                  hint="OAuth scopes to request during consent."
                  suggestions={[
                    {
                      value: 'offline_access',
                      hint: 'offline_access keeps the connection alive — without it, it expires with the access token (often an hour) and each member has to reconnect.',
                    },
                  ]}
                />
              </div>
            </Card>

            <Card>
              <button
                type="button"
                onClick={() => setManualOpen((v) => !v)}
                className="flex w-full items-center gap-2 text-left"
              >
                {manualOpen ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
                <span className="text-sm font-medium text-foreground">
                  Advanced — bring your own OAuth client
                </span>
              </button>
              {manualOpen && (
                <div className="mt-4 space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Most servers support Dynamic Client Registration and don't
                    need this. Provide credentials only if the server's OAuth
                    provider requires a pre-registered client.
                  </p>
                  <Input
                    label="Client ID"
                    value={clientId}
                    onChange={(e: { target: { value: string } }) =>
                      setClientId(e.target.value)
                    }
                    placeholder="OAuth client id"
                    autoComplete="off"
                  />
                  <Input
                    label="Client Secret"
                    type="password"
                    value={clientSecret}
                    onChange={(e: { target: { value: string } }) =>
                      setClientSecret(e.target.value)
                    }
                    placeholder="OAuth client secret"
                    autoComplete="new-password"
                    hint={
                      isEditing
                        ? 'Leave blank to keep the existing secret.'
                        : undefined
                    }
                  />
                </div>
              )}
            </Card>
          </>
        )}

        <div className="flex justify-end gap-4">
          <Link
            to={
              isEditing && serverId
                ? routes.console.spaceToolsMcpServer(spaceId, serverId)
                : routes.console.spaceToolsMcpServers(spaceId)
            }
          >
            <Button type="button" variant="secondary">
              Cancel
            </Button>
          </Link>
          <Button type="submit" isLoading={submitting} disabled={!canSubmit}>
            {isEditing ? 'Save changes' : 'Add MCP Server'}
          </Button>
        </div>
      </form>
    </div>
  );
}
